import { Box, Button, Dialog, Grid, LinearProgress } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListResponseOfSkuLearningObjective,
  SkuLearningObjective,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import SkuLearningObjectiveBox from "./SkuLearningObjectiveBox";
import SkuLearningObjectiveForm from "./SkuLearningObjectiveForm";

const useStyles = (_: Theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    width: "100%",
    height: "20vh",
    overflow: "auto",
  },
});

const SkuLearningObjectives = ({ sku, ...props }: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const styles = useStyles(useTheme());
  const [createOpen, createOpenActions] = useBoolean(false);

  const {
    data,
    isFetching,
  } = useStrictQuery<ListResponseOfSkuLearningObjective>(
    ["skuLearningObjectives", sku.id!],
    () =>
      skuService.getSkuLearningObjectives(
        sku.id!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuLearningObjectives = useMemo(
    () => (data?.data ?? []) as SkuLearningObjective[],
    [data?.data]
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (id)
        return registerPromise(skuService.deleteSkuLearningObjective(id))
          .then(() => {
            queryClient.invalidateQueries(["skuLearningObjectives", sku.id!]);

            setSnackbarMessage(
              new SnackbarMessageProps(
                t({ id: "skuLearningObjectiveDeleted" }),
                Severity.Warning
              )
            );
          })
          .catch((err: any) => {
            console.log(err);
          });
    },
    [queryClient, registerPromise, sku.id, skuService, t]
  );

  return (
    <>
      <Box sx={styles.root}>
        {isFetching && <LinearProgress color="secondary" />}
        <Box mb={1}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button
                  variant="outlined"
                  type="submit"
                  color="primary"
                  disabled={isFetching}
                  onClick={createOpenActions.setTrue}
                  data-cy="addLearnigObjectiveButton"
                >
                  <T id="skuLearningObjectiveCreateButtonLabel" />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {skuLearningObjectives.map((skuLearningObjective, i) => {
          return (
            <Grid key={skuLearningObjective?.id} container spacing={4}>
              <Grid item xs={12}>
                <SkuLearningObjectiveBox
                  skuLearningObjective={skuLearningObjective}
                  deleteLearningObjective={handleDelete}
                  sku={sku}
                  index={i}
                  {...props}
                ></SkuLearningObjectiveBox>
              </Grid>
            </Grid>
          );
        })}
        <SnackbarMessage
          snackbarMessage={snackbarMessage}
          setSnackbarMessage={setSnackbarMessage}
        />
      </Box>
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuLearningObjectiveForm
          onClose={createOpenActions.setFalse}
          sku={sku}
          submitLabel={t({ id: "skuLearningObjectiveCreateButton" })}
          {...props}
        />
      </Dialog>
    </>
  );
};

export default withErrorBoundary(SkuLearningObjectives);
